import React, { useState, useEffect } from "react";
import SearchFilter from "./SearchFilter";
import PsikologCard from "./PsikologCard";
import BookingDetail from "./BookingDetail";
import ModalPsikologDetail from "./ModalPsikologDetail";
import {
  getAvailablePsikologDetail,
  getAvailablePsikolog,
} from "../api/appointment";

function Step3Booking({ psikologData: initialData, prevStep }) {
  const [showDetail, setShowDetail] = useState(false);
  const [bookingData, setBookingData] = useState(null);
  const [loading, setLoading] = useState(false);

  // State untuk menyimpan filter yang sedang aktif
  const [filterData, setFilterData] = useState({
    date: new Date().toISOString().split("T")[0], // Default hari ini
    bahasa: "",
    name: "",
  });

  // State terpisah untuk list utama dan list rekomendasi
  const [listPsikolog, setListPsikolog] = useState([]);
  const [listRekomendasi, setListRekomendasi] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [selectedPsikolog, setSelectedPsikolog] = useState(null);

  useEffect(() => {
    // Menyesuaikan dengan format JSON baru dari backend
    if (initialData && initialData.psikolog_list) {
      setListPsikolog(initialData.psikolog_list || []);
      setListRekomendasi(initialData.rekomendasi_psikolog || []);
    } else if (Array.isArray(initialData)) {
      // Fallback jika API lama (yang langsung return array) masih terpanggil
      setListPsikolog(initialData || []);
      setListRekomendasi([]);
    }
  }, [initialData]);

  const handleFilterSearch = async (formData) => {
    setLoading(true);
    setFilterData(formData);
    const params = new URLSearchParams(window.location.search);
    const userRole = params.get("user"); // Hasilnya: "admin" atau null
    try {
      const res = await getAvailablePsikolog({
        date: formData.date || "",
        bahasa: formData.bahasa || "",
        name: formData.name || "",
        user: userRole,
      });

      if (res.status === true) {
        // Update kedua state dari respons JSON backend yang baru
        setListPsikolog(res.data.psikolog_list || []);
        setListRekomendasi(res.data.rekomendasi_psikolog || []);
      } else {
        alert(res.msg || "Psikolog tidak ditemukan");
      }
    } catch (err) {
      console.error(err);
      alert("Terjadi kesalahan saat memfilter data");
    } finally {
      setLoading(false);
    }
  };

  const handleShowBookingDetail = async (psikolog) => {
    // Memastikan loading aktif sebelum proses await dimulai
    setLoading(true);

    try {
      const params = new URLSearchParams(window.location.search);
      const userRole = params.get("user");

      // 1. Tentukan tanggal target
      const targetDate =
        filterData.date ||
        psikolog.available_dates?.find(
          (d) => d.first_slot?.status === "available",
        )?.date;

      // Request API Detail
      const res = await getAvailablePsikologDetail({
        psikolog_id: psikolog.id,
        date: targetDate,
        user: userRole,
      });

      if (res.status === true) {
        const enhancedBookingData = {
          ...res.data,
          date: targetDate,
        };

        setBookingData(enhancedBookingData);
        setShowDetail(true);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        alert(res.msg || "Gagal mengambil detail booking");
      }
    } catch (err) {
      alert(err?.response?.data?.msg || "Terjadi kesalahan");
    } finally {
      // Loading dimatikan HANYA setelah semua proses selesai
      setLoading(false);
    }
  };

  const handleHideBookingDetail = () => {
    setShowDetail(false);
    setBookingData(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Helper function untuk merender card agar tidak copas kode berkali-kali
  const renderPsikologCards = (psikologArray) => {
    return psikologArray.map((item) => {
      const psikolog = {
        id: item.psikolog_id,
        name: item.psikolog_name,
        bahasa: item.psikolog_bahasa,
        description: item.psikolog_description,
        foto: item.psikolog_foto,
        available_dates: item.available_dates,
      };

      return (
        <PsikologCard
          key={psikolog.id}
          psikolog={psikolog}
          onBook={() => handleShowBookingDetail(psikolog)}
          onViewProfile={() => {
            setSelectedPsikolog(psikolog);
            setShowModal(true);
          }}
          loading={loading}
          selectedDate={filterData.date}
        />
      );
    });
  };

  return (
    <div className="step-content active" style={{ position: "relative" }}>
      {/* FULL SCREEN LOADING OVERLAY */}
      {loading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            backdropFilter: "blur(2px)",
          }}
        >
          <div
            className="spinner-border text-primary"
            role="status"
            style={{ width: "3rem", height: "3rem" }}
          >
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      {!showDetail ? (
        <div
          className="content mx-auto content-book"
          style={{ maxWidth: "1200px" }}
        >
          <h5 className="fw-semibold text-brand mb-3">
            Cari Psikolog Tes Bakat Indonesia
          </h5>

          <SearchFilter onSearch={handleFilterSearch} />

          <hr className="my-4" />

          {/* ========================================= */}
          {/* BAGIAN REKOMENDASI PSIKOLOG                 */}
          {/* ========================================= */}
          {listRekomendasi && listRekomendasi.length > 0 && (
            <div className="mb-5">
              <h6 className="fw-bold mb-3 text-success">
                {loading ? "Memperbarui data..." : "Rekomendasi Psikolog"}
              </h6>
              <div className="row g-4">
                {renderPsikologCards(listRekomendasi)}
              </div>
              <hr className="mt-5 mb-4" />
            </div>
          )}

          {/* ========================================= */}
          {/* BAGIAN DAFTAR PSIKOLOG UTAMA                */}
          {/* ========================================= */}
          <h6 className="fw-semibold mb-3">
            {loading
              ? "Memperbarui data..."
              : (listRekomendasi && listRekomendasi.length > 0 ? "Psikolog Lainnya" : "Daftar Psikolog")
            }
          </h6>

          <div className="row g-4">
            {Array.isArray(listPsikolog) && listPsikolog.length > 0 ? (
              renderPsikologCards(listPsikolog)
            ) : (
              <div className="col-12 text-center py-5">
                <p className="text-muted">
                  Psikolog tidak tersedia untuk kriteria tersebut.
                </p>
              </div>
            )}
          </div>

          <br />

          <button
            type="button"
            className="btn btn-secondary btn-footer"
            onClick={prevStep}
            disabled={loading}
          >
            <i className="bi bi-arrow-left-short"></i> Kembali
          </button>
        </div>
      ) : (
        <BookingDetail
          bookingData={bookingData}
          onBack={handleHideBookingDetail}
          onViewProfile={() => {
            setSelectedPsikolog(bookingData?.psikolog);
            setShowModal(true);
          }}
        />
      )}

      <ModalPsikologDetail
        show={showModal}
        onHide={() => setShowModal(false)}
        psikolog={selectedPsikolog}
      />
    </div>
  );
}


export default Step3Booking;